/**
 * brandDetection.ts
 * -----------------
 * Centralised brand detection from the current URL or request headers.
 *
 * No hardcoded brand list — valid brands are derived from `configService.ts`
 * (BRAND_OVERRIDES), the single source of truth.
 *
 * Caddy always normalises brand subdomains into path segments
 * (e.g. gtr.nestingale.com/products → /showroom/gtr/products),
 * so the first path segment is the primary detection mechanism.
 *
 * Resolution order:
 *   1. First path segment  — /gtr/products/...   → 'gtr'
 *   2. Hostname subdomain  — gtr.nestingale.com  → 'gtr' (fallback for API routes)
 *   3. Fallback            — 'nestingale'
 */
import type { BrandKey } from './configService';
import { isBrandKey } from './configService';

/**
 * Extract brand from a pathname string.
 * Works with both browser paths and server-side paths.
 *
 *   getBrandFromPath('/gtr/products/rugs')  → 'gtr'
 *   getBrandFromPath('/products/rugs')      → 'nestingale'
 */
export function getBrandFromPath(pathname: string): BrandKey {
  const firstSegment = pathname.split('/').filter(Boolean)[0] || '';
  return isBrandKey(firstSegment) ? firstSegment : 'nestingale';
}

/**
 * Detect brand from a hostname string (for API routes where path may not have vendor).
 *
 *   getBrandFromHostname('gtr.nestingale.com') → 'gtr'
 *   getBrandFromHostname('nestingale.com')     → 'nestingale'
 */
export function getBrandFromHostname(hostname: string): BrandKey {
  const parts = hostname.split('.');
  if (parts.length > 1) {
    const subdomain = parts[0];
    if (isBrandKey(subdomain)) return subdomain;
  }
  return 'nestingale';
}

/**
 * Detect the active brand from `window.location`.
 * Safe to call at module level, in useEffect, or in event handlers.
 * Returns `'nestingale'` on the server (SSR).
 *
 * Checks path first (Caddy-normalised), then hostname subdomain as fallback.
 */
export function getBrand(): BrandKey {
  if (typeof window === 'undefined') return 'nestingale';

  const { hostname, pathname } = window.location;

  // 1. Path segment (primary — Caddy always normalises subdomains into path)
  const fromPath = getBrandFromPath(pathname);
  if (fromPath !== 'nestingale') return fromPath;

  // 2. Hostname subdomain (fallback for direct subdomain access)
  return getBrandFromHostname(hostname);
}
